import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Alert,
  Chip,
  LinearProgress,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { 
  Edit as EditIcon, 
  ArrowForward as ArrowForwardIcon, 
  Upload as UploadIcon,
  Save as SaveIcon 
} from '@mui/icons-material';
import CodeMirror from '@uiw/react-codemirror';
import { markdown } from '@codemirror/lang-markdown';
import ReactMarkdown from 'react-markdown';

const ExtractPRD = ({
  extractedPRD,
  editMode,
  editContent,
  onEditToggle,
  onSaveEdit,
  onNext,
  openDialog,
  documentId,
  loading
}) => {
  const [showPreview, setShowPreview] = useState(false);
  const [previewContent, setPreviewContent] = useState('');
  const [extractionProgress, setExtractionProgress] = useState(0);
  const [localEditContent, setEditContent] = useState(editContent || '');

  const handlePreview = (content) => {
    setPreviewContent(typeof content === 'string' ? content : '');
    setShowPreview(true);
  };

  const handleExtract = () => {
    // Simulate extraction progress
    setExtractionProgress(0);
    const interval = setInterval(() => {
      setExtractionProgress((prev) => {
        if (prev >= 100) {
          clearInterval(interval);
          return 100;
        }
        return prev + 10;
      });
    }, 500);

    // Call the actual extraction
    openDialog('extractPRD');
  };

  // Ensure content is always a string
  const safeContent = typeof editContent === 'string' ? editContent : '';
  const safeExtractedContent = typeof extractedPRD?.content === 'string' ? extractedPRD.content : '';

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        {documentId ? 'Load Existing PRD Document' : 'Extract PRD from Project Conversations'}
      </Typography>
      
      {loading ? (
        <Box mt={2}>
          <Paper elevation={2} sx={{ p: 3, mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Loading PRD Document...
            </Typography>
            <LinearProgress sx={{ mt: 2 }} />
          </Paper>
        </Box>
      ) : extractedPRD ? (
        <Box mt={2}>
          <Paper elevation={2} sx={{ p: 3, mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              {extractedPRD.title || 'Product Requirements Document'}
              <Chip 
                label={documentId ? 'PRD Loaded' : 'PRD Extracted'} 
                color="success" 
                size="small" 
                sx={{ ml: 2 }}
              />
            </Typography>
            
            {editMode ? (
              <Box mt={2}>
                <CodeMirror
                  value={safeContent}
                  height="400px"
                  onChange={(value) => setEditContent(value)}
                  extensions={[markdown()]}
                />
                <Box mt={2} display="flex" justifyContent="flex-end">
                  <Button 
                    variant="outlined" 
                    onClick={() => onEditToggle(false)}
                    sx={{ mr: 1 }}
                  >
                    Cancel
                  </Button>
                  <Button 
                    variant="contained" 
                    onClick={() => onSaveEdit(localEditContent)}
                    startIcon={<SaveIcon />}
                  >
                    Save
                  </Button>
                </Box>
              </Box>
            ) : (
              <>
                <Box sx={{ maxHeight: '400px', overflow: 'auto', mt: 2 }}>
                  <ReactMarkdown>
                    {safeExtractedContent}
                  </ReactMarkdown>
                </Box>
                <Box mt={2} display="flex" justifyContent="flex-end">
                  <Button 
                    variant="outlined" 
                    onClick={() => onEditToggle(safeExtractedContent)}
                    startIcon={<EditIcon />}
                    sx={{ mr: 1 }}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="contained"
                    onClick={onNext}
                    endIcon={<ArrowForwardIcon />}
                  >
                    Continue to Field Validations
                  </Button>
                </Box>
              </>
            )}
          </Paper>
        </Box>
      ) : (
        <Box mt={2}>
          <Alert severity="info" sx={{ mb: 2 }}>
            {documentId 
              ? 'Load the existing PRD document to continue with the workflow.'
              : 'Extract the PRD content from your project conversations. The system will analyze the conversation history and create a structured PRD.'}
          </Alert>
          
          <Paper elevation={2} sx={{ p: 3, mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              {documentId ? 'Existing PRD Document' : 'Project Conversations'}
            </Typography>
            
            <Box mt={2}>
              <Button
                variant="contained"
                onClick={handleExtract}
                startIcon={documentId ? <UploadIcon /> : <EditIcon />}
                fullWidth
              >
                {documentId ? 'Load Existing PRD' : 'Extract PRD from Conversations'}
              </Button>
              
              {extractionProgress > 0 && extractionProgress < 100 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {documentId ? 'Loading PRD document...' : 'Extracting PRD from conversations...'}
                  </Typography>
                  <LinearProgress 
                    variant="determinate" 
                    value={extractionProgress} 
                    sx={{ height: 10, borderRadius: 5 }}
                  />
                  <Typography variant="body2" color="text.secondary" align="right" sx={{ mt: 0.5 }}>
                    {Math.round(extractionProgress)}%
                  </Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </Box>
      )}

      {/* Preview Dialog */}
      <Dialog
        open={showPreview}
        onClose={() => setShowPreview(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Preview Extracted PRD</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <ReactMarkdown>
              {previewContent}
            </ReactMarkdown>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPreview(false)}>
            Close
          </Button>
          <Button 
            variant="contained" 
            onClick={() => {
              setShowPreview(false);
              openDialog('extractPRD');
            }}
          >
            Extract PRD
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ExtractPRD; 